import React from 'react'
import { makeObservable, observable, action } from 'mobx'
import * as R from 'ramda'
import moment from 'moment'

import LicenceGroup from './models/LicenceGroup'
import LicenceAllocation from './models/LicenceAllocation'

import { getProductMap } from '../../../utils/helpers'

class Store {
    appStore

    @observable loading = true

    @observable licenceGroups = []

    @observable licenceAllocations = []

    @observable clients = []

    @observable clientId

    @observable activeTab = 'licenceGroups'

    @observable clientProducts = []

    @observable industries = []

    @observable eventTypes = []

    @observable licenceAllocation = {}

    @observable licenceGroup = {}

    licenceAllocationColumns = [
        {
            name: 'id',
            key: 'id',
        },
        {
            name: 'Licence Group',
            key: 'groupId',
            cellClass: 'bold',
            dataTransform: (groupId) => (
                this.licenceGroups.find(({ id }) => (id === groupId))?.name ?? groupId
            ),
        },
        {
            name: 'Product',
            key: 'productId',
            cellClass: 'bold',
            dataTransform: (productId) => (
                this.clientProducts.find(({ value }) => (value === productId))?.label ?? productId
            ),
        },
        {
            name: 'Is licence excempt',
            key: 'licenceExcempt',
            cellClass: 'bold',
            dataTransform: (value) => (
                value ? 'true' : 'false'
            ),
        },
        {
            name: 'Allocated at',
            key: 'allocatedAt',
            cellClass: 'bold',
        },
        {
            name: '',
            key: 'id',
            cellClass: 'bold overflow',
            dataTransform: (_, row) => (
                <div className="campaign-links-table-actions">
                    <i className="fal fa-edit" onClick={() => {
                        this.initLicenceAllocation(row)
                        this.toggleModal('licenceAllocation')
                    }} />
                </div>
            ),
        },
    ]

    licenceGroupColumns = [
        {
            name: 'id',
            key: 'id',
        },
        {
            name: 'Name',
            key: 'name',
            cellClass: 'bold',
        },
        {
            name: 'Is licence excempt',
            key: 'licenceExcempt',
            cellClass: 'bold',
            dataTransform: (value) => (
                value ? 'true' : 'false'
            ),
        },
        {
            name: 'Industry',
            key: 'industry',
            cellClass: 'bold',
        },
        {
            name: 'Event type',
            key: 'eventType',
            cellClass: 'bold',
        },
        {
            name: 'Activation start date',
            key: 'startDate',
            cellClass: 'bold',
        },
        {
            name: 'Back stop expiry',
            key: 'expiryDate',
            cellClass: 'bold',
        },
        {
            name: 'Licence Period',
            key: 'period',
            cellClass: 'bold',
        },
        {
            name: 'Available licences',
            key: 'licenceCount',
            cellClass: 'bold',
            dataTransform: (_, row) => (
                row.licenceCount - row.licenceUsed
            ),
        },
        {
            name: 'Allocated licences',
            key: 'licenceUsed',
            cellClass: 'bold',
        },
        {
            name: 'Is active',
            key: 'isActive',
            cellClass: 'bold',
            dataTransform: (value) => (
                value ? 'true' : 'false'
            ),
        },
        {
            name: '',
            key: 'id',
            cellClass: 'bold overflow',
            dataTransform: (_, row) => (
                <div className="campaign-links-table-actions">
                    <i className="fal fa-edit" onClick={() => {
                        this.initLicenceGroup(row)
                        this.toggleModal('licenceGroup')
                    }} />
                </div>
            ),
        },
    ]

    tabs = [
        {
            key: 'licenceGroups',
            label: 'Licence groups',
            onClick: async () => {
                this.licenceGroups = []
                this.activeTab = 'licenceGroups'
                await this.getLicenceGroups(this.clientId)
            },
        },
        {
            key: 'productLicences',
            label: 'Product licences',
            onClick: async () => {
                this.activeTab = 'productLicences'
                await this.getProductLicences(this.clientId)
            },
        },
    ]

    @observable modals = {
        licenceGroup: false,
        licenceAllocation: false,
    }

    licenceAddons = [
        {
            value: 'hasFilterableDashboard',
            label: 'Filterable dashboards',
        },
        {
            value: 'hasEmailDistribution',
            label: 'Email distribution',
        },
    ]

    constructor(appStore) {
        makeObservable(this)
        this.appStore = appStore
    }

    init = async () => {
        const { clients } = await this.appStore.http.get('/client')
        this.clients = clients.map(({ clientId, name }) => ({
            value: clientId,
            label: `[${clientId}] - ${name}`,
        }))
    }

    changeTab = (tab) => {
        this.activeTab = tab
    }

    tabIsActive = (tab) => this.activeTab === tab

    toggleModal = (modal) => {
        this.modals[modal] = !this.modals[modal]

        if (!this.modals[modal]) {
            this.licenceGroup = {}
            this.licenceAllocation = {}
        }
    }

    getClientProducts = async (clientId) => {
        const clientProducts = await this.appStore.http.get(`/product/client/${clientId}`)
        this.clientProducts = clientProducts.map(({ productId, name, edition }) => ({
            value: productId,
            label: `${name}${R.isEmpty(edition) ? '' : ` - ${edition}`}`,
        }))
    }

    getProductMap = async (clientId) => {
        const productMap = await getProductMap(this.appStore.http, clientId)

        this.industries = Object.values(productMap)
    }

    @action
    changeClient = async ({ value }) => {
        this.clientId = value
        await Promise.all([
            this.getLicenceGroups(value),
            this.getClientProducts(value),
            this.getProductMap(value),
        ])
        this.changeTab('licenceGroups')
    }

    /** Licence group */
    initLicenceGroup = (licenceGroup = { clientId: this.clientId }) => {
        this.licenceGroup = new LicenceGroup(licenceGroup)
    }

    getLicenceGroups = async (clientId) => {
        const licenceGroups = await this.appStore.http.get(`/intranet/licencegroup/${clientId}`)

        this.licenceGroups = licenceGroups.map((group) => {
            const { startDate, period } = group
            const now = moment()
            const isActive = moment(startDate).isBefore(now) && now.isBefore(moment(startDate).add(period, 'M'))
            const isFuture = moment(startDate).isAfter(now)
            return {
                ...group,
                isActive,
                isFuture,
            }
        })
    }

    createLicenceGroup = () => {
        this.initLicenceGroup()
        this.toggleModal('licenceGroup')
    }

     @action
    updateLicenceGroup = ({ value, name, textFormat }) => {
        // dates use textFormat
        this.licenceGroup[name] = textFormat ?? value

        if (name === 'industry') {
            const eventTypes = this.industries.find((industry) => industry.value === value).children
            this.eventTypes = Object.values(eventTypes).map(({ value, productTypeNameOnly }) => ({
                value,
                label: productTypeNameOnly,
            }))
            this.licenceGroup.eventType = undefined
        }

        if (name === 'licenceExcempt') {
            this.licenceGroup.licenceExcemptReason = undefined
        }
    }

    saveLicenceGroup = async () => {
        try {
            const isNew = R.isNil(this.licenceGroup.id)
            const method = isNew ? 'post' : 'put'
            const url = isNew
                ? '/intranet/licencegroup'
                : `/intranet/licencegroup/${this.licenceGroup.id}`

            await this.appStore.http[method](url, JSON.stringify(this.licenceGroup))
            this.toggleModal('licenceGroup')
            this.getLicenceGroups(this.clientId)
            this.appStore.addNotification(null, 'Licence group saved', 'success')
        } catch (e) {
            console.error(e)
            this.appStore.addNotification(null, 'Error creating licence group', 'error')
        }
    }

    /** Licence allocation */
    initLicenceAllocation = (licenceAllocation = {}) => {
        this.licenceAllocation = new LicenceAllocation(licenceAllocation)
    }

    getProductLicences = async (clientId) => {
        this.licenceAllocations = await this.appStore.http.get(`/intranet/licenceallocation/${clientId}`)
    }

    getLicenceGroupsForAllocation = (productId) => {
        const { licenceGroups } = this
        // TODO - filter licence groups by products productType
        return licenceGroups
            .filter(({ isActive, isFuture }) => isActive || isFuture)
            .map((obj) => ({
                value: obj.id,
                label: obj.name,
            }))
    }

    createlicenceAllocation = () => {
        this.initLicenceAllocation()
        this.toggleModal('licenceAllocation')
    }

     @action
    updateLicenceAllocation = ({ value, name }) => {
        this.licenceAllocation[name] = value

        if (name === 'groupId') {
            this.licenceAllocation.feature = []
        }

        if (name === 'licenceExcempt') {
            this.licenceAllocation.licenceExcemptReason = undefined
        }
    }

    saveLicenceAllocation = async () => {
        try {
            const isNew = R.isNil(this.licenceAllocation.id)
            const method = isNew ? 'post' : 'put'
            const url = isNew
                ? '/intranet/licenceallocation'
                : `/intranet/licenceallocation/${this.licenceAllocation.id}`

            console.log('saveLicenceALlocation', isNew, method, url, JSON.stringify(this.licenceAllocation))
            await this.appStore.http[method](url, JSON.stringify(this.licenceAllocation))
            this.toggleModal('licenceAllocation')
            this.getProductLicences(this.clientId)
            this.appStore.addNotification(null, 'Licence allocation saved', 'success')
        } catch (e) {
            console.error(e)
            this.appStore.addNotification(null, 'Error creating licence allocation', 'error')
        }
    }
}

export default Store
